import { useState, useEffect } from 'react';
import { 
  FileDoc, 
  FilePdf, 
  Folder,
  Plus, 
  Buildings,
  Phone,
  EnvelopeSimple,
  User,
  ArrowRight,
  CheckCircle,
  Clock,
  Warning,
  CloudArrowUp
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import DPGFEditor from './DPGFEditor';
import CCTPEditor from './CCTPEditor';
import PlansArborescence from './PlansArborescence';
import DpgfDceConsistencyBanner from './DpgfDceConsistencyBanner';
import { BOUTONS } from '../../lib/libelles';

const dossierTypes = [
  { id: 'dpgf', label: 'DPGF', desc: 'Décomposition du Prix Global et Forfaitaire', icon: FilePdf, color: 'text-[#C75B39]', bgColor: 'bg-[#FFF3E0]', clickable: true },
  { id: 'cctp', label: 'CCTP', desc: 'Cahier des Clauses Techniques Particulières', icon: FileDoc, color: 'text-[#1E3A5F]', bgColor: 'bg-[#E3F2FD]', clickable: true },
  // R5 / RP61 (D26 A) — la consultation (DCE, offres, attribution) est dans l'onglet Marchés :
  // Conception n'en garde qu'un lien.
  { id: 'dce', label: 'Consultation → Marchés', desc: "Le DCE, les offres et l'attribution se suivent lot par lot dans l'onglet Marchés", icon: Folder, color: 'text-[#D4A84B]', bgColor: 'bg-[#FFF8E1]', clickable: true },
  { id: 'plans', label: 'Plans', desc: 'Plans interactifs avec pièces et annotations', icon: FilePdf, color: 'text-[#2D6A4F]', bgColor: 'bg-[#E8F5E9]', clickable: true },
];

const permisStatuts = {
  non_depose: { label: 'Non déposé', color: 'text-[#6B6B6B]', bg: 'bg-[#F5F5F3]' },
  depose: { label: 'Déposé', color: 'text-[#B8860B]', bg: 'bg-[#FFF8E1]' },
  en_instruction: { label: 'En instruction', color: 'text-[#1E3A5F]', bg: 'bg-[#E3F2FD]' },
  accorde: { label: 'Accordé', color: 'text-[#2D6A4F]', bg: 'bg-[#E8F5E9]' },
  refuse: { label: 'Refusé', color: 'text-[#8B3A3A]', bg: 'bg-[#FFEBEE]' },
  complement: { label: 'Demande de complément', color: 'text-[#B8860B]', bg: 'bg-[#FFF8E1]' },
};

// RP03 / D38 — Le suivi des autorisations d'urbanisme (type et statut du
// permis, retours et contact mairie) affichait « enregistré » sans rien
// enregistrer. Il est masqué tant qu'aucun client ne le demande ; l'arrêté de
// permis se range dans les documents. Le code et l'API /autorisations sont
// conservés : repasser ce drapeau à true pour le réafficher une fois branché.
const URBANISME_ACTIF = false;

export default function ProjectConception({ chantierId, chantierName, onNavigateTab, onImporter }) {
  // Fluidité B2 (P2#17) — l'ancien bouton « Importer une DPGF (Word) » est remplacé par
  // l'entrée unique « Importer » (components/project/ImporterDialog.jsx), qui garde la
  // même route (POST /dce/import) et la même règle : proposée seulement à l'architecte
  // et au chef de projet (RP02). `onImporter` n'est fourni que si un import est permis.
  const [activeView, setActiveView] = useState('main');
  const [permisType, setPermisType] = useState('permis_construire');
  const [permisStatut, setPermisStatut] = useState('non_depose');
  const [depotDate, setDepotDate] = useState('');
  const [permisNumero, setPermisNumero] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [mairieInfo, setMairieInfo] = useState({
    nom: '', adresse: '', telephone: '', email: '', charge_affaire: '', charge_email: '', charge_telephone: ''
  });
  const [editMairieOpen, setEditMairieOpen] = useState(false);
  const [editMairieForm, setEditMairieForm] = useState({...mairieInfo});
  const [retoursMairie, setRetoursMairie] = useState([]);
  const [addRetourOpen, setAddRetourOpen] = useState(false);
  const [newRetour, setNewRetour] = useState({ date: '', type: '', contenu: '' });

  // Load autorisation from API
  useEffect(() => {
    if (!URBANISME_ACTIF) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/autorisations`, { withCredentials: true });
        if (data.length > 0) {
          const auth = data[0];
          setPermisType(auth.type === 'PC' ? 'permis_construire' : auth.type === 'DP' ? 'declaration_prealable' : 'permis_amenager');
          setPermisStatut(auth.statut || 'en_preparation');
          setDepotDate(auth.date_depot || '');
          setPermisNumero(auth.numero_dossier || '');
          if (auth.contact_mairie) setMairieInfo(auth.contact_mairie);
          if (auth.observations) setRetoursMairie(JSON.parse(auth.observations || '[]'));
        }
      } catch { /* */ }
    })();
  }, [chantierId]);

  // Dossier progress removed per user request

  const handleSaveMairie = () => {
    setMairieInfo({...editMairieForm});
    setEditMairieOpen(false);
    toast.success('Informations mairie mises à jour');
  };

  const handleAddRetour = (e) => {
    e.preventDefault();
    const retour = {
      id: Date.now(),
      ...newRetour,
      statut: newRetour.type.includes('complémentaire') || newRetour.type.includes('Action') ? 'action' : 'info'
    };
    setRetoursMairie([retour, ...retoursMairie]);
    setAddRetourOpen(false);
    setNewRetour({ date: '', type: '', contenu: '' });
    toast.success('Retour mairie ajouté');
  };

  const statusConfig = permisStatuts[permisStatut] || permisStatuts.non_depose;

  // DPGF Editor view
  if (activeView === 'dpgf') {
    return <DPGFEditor chantierId={chantierId} chantierName={chantierName || ''} onBack={() => setActiveView('main')} onNavigateCCTP={() => setActiveView('cctp')} />;
  }

  // CCTP Editor view
  if (activeView === 'cctp') {
    return <CCTPEditor chantierId={chantierId} chantierName={chantierName || ''} onBack={() => setActiveView('main')} onNavigateDPGF={() => setActiveView('dpgf')} />;
  }

  // Plans Editor view
  if (activeView === 'plans') {
    return <PlansArborescence chantierId={chantierId} onBack={() => setActiveView('main')} />;
  }

  return (
    <div data-testid="project-conception">
      {/* Cohérence DPGF↔DCE v31 — bandeau alerte */}
      <DpgfDceConsistencyBanner chantierId={chantierId} />
      <div className={`grid grid-cols-1 gap-6 ${URBANISME_ACTIF ? 'lg:grid-cols-3' : ''}`}>
        {/* Main Content */}
        <div className={`space-y-6 ${URBANISME_ACTIF ? 'lg:col-span-2' : ''}`}>
          {/* Dossier Constitution Progress */}
          <div className="card-kroquis">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Constitution du dossier
              </h3>
              <div className="flex items-center gap-2">
                {onImporter && (
                <Button variant="outline" className="rounded-none gap-2 text-sm text-[#1E3A5F] border-[#1E3A5F]" data-testid="import-dce-button" onClick={onImporter}>
                  <CloudArrowUp size={16} />
                  {BOUTONS.importer}
                </Button>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {dossierTypes.map(dossier => {
                const Icon = dossier.icon;
                return (
                  <div 
                    key={dossier.id} 
                    className={`border border-[#E0E0DB] p-4 hover:border-[#2C2C2C] transition-colors cursor-pointer ${dossier.clickable ? 'hover:shadow-md' : ''}`} 
                    data-testid={`dossier-${dossier.id}`}
                    onClick={() => {
                      if (dossier.id === 'dpgf') setActiveView('dpgf');
                      else if (dossier.id === 'cctp') setActiveView('cctp');
                      else if (dossier.id === 'dce') { if (onNavigateTab) onNavigateTab('marches'); }
                      else if (dossier.id === 'plans') setActiveView('plans');
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 flex items-center justify-center ${dossier.bgColor} ${dossier.color}`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-[#2C2C2C]">{dossier.label}</h4>
                          {dossier.clickable && <ArrowRight size={14} className="text-[#C75B39]" />}
                        </div>
                        <p className="text-xs text-[#6B6B6B]">{dossier.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {URBANISME_ACTIF && (<>
          {/* Permis de Construire / Déclaration Préalable */}
          <div className="card-kroquis">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Suivi des autorisations d'urbanisme
              </h3>
              <span className={`text-xs px-3 py-1 font-bold uppercase tracking-wider ${statusConfig.bg} ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs text-[#6B6B6B]">Type de demande</Label>
                <Select value={permisType} onValueChange={setPermisType}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="permis_construire">Permis de construire</SelectItem>
                    <SelectItem value="declaration_prealable">Déclaration préalable</SelectItem>
                    <SelectItem value="permis_amenager">Permis d'aménager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B6B6B]">Statut</Label>
                <Select value={permisStatut} onValueChange={setPermisStatut}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(permisStatuts).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B6B6B]">Date de dépôt</Label>
                <Input type="date" value={depotDate} onChange={e => setDepotDate(e.target.value)} className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6B6B]">N de dossier</Label>
                <Input value={permisNumero} onChange={e => setPermisNumero(e.target.value)} placeholder="PC 092-046-26-00012" className="rounded-none mt-1" />
              </div>
            </div>
          </div>

          {/* Retours Mairie */}
          <div className="card-kroquis">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Retours Mairie
              </h3>
              <Dialog open={addRetourOpen} onOpenChange={setAddRetourOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2 text-sm" data-testid="add-retour-mairie">
                    <Plus size={16} weight="bold" />
                    Ajouter un retour
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Nouveau retour mairie</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddRetour} className="space-y-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={newRetour.date} onChange={e => setNewRetour({...newRetour, date: e.target.value})} required className="rounded-none" />
                    </div>
                    <div>
                      <Label>Type de retour</Label>
                      <Select value={newRetour.type} onValueChange={v => setNewRetour({...newRetour, type: v})}>
                        <SelectTrigger className="rounded-none"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Accusé de réception">Accusé de réception</SelectItem>
                          <SelectItem value="Demande de pièces complémentaires">Demande de pièces complémentaires</SelectItem>
                          <SelectItem value="Notification de décision">Notification de décision</SelectItem>
                          <SelectItem value="Prolongation de délai">Prolongation de délai</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Contenu / Détails</Label>
                      <Textarea value={newRetour.contenu} onChange={e => setNewRetour({...newRetour, contenu: e.target.value})} rows={3} required className="rounded-none" placeholder="Détails du retour..." />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setAddRetourOpen(false)} className="rounded-none">Annuler</Button>
                      <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Ajouter</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {retoursMairie.length === 0 ? (
              <p className="text-[#6B6B6B] text-center py-8">Aucun retour mairie pour le moment</p>
            ) : (
              <div className="space-y-3">
                {retoursMairie.map(retour => (
                  <div key={retour.id} className={`border p-4 ${retour.statut === 'action' ? 'border-[#B8860B] bg-[#FFFBF0]' : 'border-[#E0E0DB]'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {retour.statut === 'action' ? (
                          <Warning size={18} className="text-[#B8860B] mt-0.5" />
                        ) : (
                          <CheckCircle size={18} className="text-[#2D6A4F] mt-0.5" />
                        )}
                        <div>
                          <p className="font-medium text-[#2C2C2C]">{retour.type}</p>
                          <p className="text-sm text-[#4A4A4A] mt-1">{retour.contenu}</p>
                        </div>
                      </div>
                      <span className="text-xs text-[#6B6B6B] flex-shrink-0 ml-4">
                        {retour.date ? new Date(retour.date).toLocaleDateString('fr-FR') : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>)}
        </div>

        {/* Sidebar — contact mairie (urbanisme, masqué par D38) */}
        {URBANISME_ACTIF && (
        <div className="space-y-6">
          {/* Mairie Contact */}
          <div className="card-kroquis">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[#2C2C2C]">Contact Mairie</h3>
              <Dialog open={editMairieOpen} onOpenChange={setEditMairieOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-none text-xs" data-testid="edit-mairie-button">
                    Modifier
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Informations Mairie</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                    <div>
                      <Label>Nom de la mairie</Label>
                      <Input value={editMairieForm.nom} onChange={e => setEditMairieForm({...editMairieForm, nom: e.target.value})} className="rounded-none" />
                    </div>
                    <div>
                      <Label>Adresse</Label>
                      <Input value={editMairieForm.adresse} onChange={e => setEditMairieForm({...editMairieForm, adresse: e.target.value})} className="rounded-none" />
                    </div>
                    <div>
                      <Label>Téléphone</Label>
                      <Input value={editMairieForm.telephone} onChange={e => setEditMairieForm({...editMairieForm, telephone: e.target.value})} className="rounded-none" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={editMairieForm.email} onChange={e => setEditMairieForm({...editMairieForm, email: e.target.value})} className="rounded-none" />
                    </div>
                    <div className="border-t border-[#E0E0DB] pt-4">
                      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2">Chargé d'affaire</p>
                    </div>
                    <div>
                      <Label>Nom</Label>
                      <Input value={editMairieForm.charge_affaire} onChange={e => setEditMairieForm({...editMairieForm, charge_affaire: e.target.value})} className="rounded-none" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={editMairieForm.charge_email} onChange={e => setEditMairieForm({...editMairieForm, charge_email: e.target.value})} className="rounded-none" />
                    </div>
                    <div>
                      <Label>Téléphone</Label>
                      <Input value={editMairieForm.charge_telephone} onChange={e => setEditMairieForm({...editMairieForm, charge_telephone: e.target.value})} className="rounded-none" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setEditMairieOpen(false)} className="rounded-none">Annuler</Button>
                      <Button onClick={handleSaveMairie} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Enregistrer</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Buildings size={16} className="text-[#C75B39] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#2C2C2C]">{mairieInfo.nom}</p>
                  <p className="text-xs text-[#6B6B6B]">{mairieInfo.adresse}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone size={14} className="text-[#6B6B6B]" />
                <span>{mairieInfo.telephone}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <EnvelopeSimple size={14} className="text-[#6B6B6B]" />
                <span className="text-[#1E3A5F]">{mairieInfo.email}</span>
              </div>
            </div>

            {mairieInfo.charge_affaire && (
              <div className="mt-4 pt-4 border-t border-[#E0E0DB]">
                <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2">Chargé d'affaire</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User size={14} className="text-[#C75B39]" />
                    <span className="font-medium">{mairieInfo.charge_affaire}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <EnvelopeSimple size={14} className="text-[#6B6B6B]" />
                    <span className="text-[#1E3A5F]">{mairieInfo.charge_email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-[#6B6B6B]" />
                    <span>{mairieInfo.charge_telephone}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

